import { useState, useEffect, useCallback, useContext, useRef } from "react";

import {
  EditOutlined,
  EyeOutlined,
  DeleteOutlined,
  CheckOutlined,
  MailOutlined,
  CloseOutlined,
  PlusOutlined,
  LinkOutlined,
  ReloadOutlined,
  DollarOutlined,
  FileSearchOutlined,
  ExperimentOutlined,
  AuditOutlined,
  QuestionCircleOutlined,
  SwapOutlined,
  StopOutlined,
  UndoOutlined,
} from "@ant-design/icons";
import { Tabs, Modal, Form, Input, Button } from "antd";
import Parse from "parse";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import InvestmentRequestModal from "./InvestmentRequestModal";
import ObjectList from "./ObjectList";
import PendingIdentitiesObjectList from "./PendingIdentitiesObjectList";
import { RoleContext, useIsAdmin, useIsReviewer } from "../context/RoleContext";
import DfnsService from "../services/DfnsService";
import { NomyxAction } from "../utils/Constants";

const { TabPane } = Tabs;

const IdentitiesPage = ({ service }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [identities, setIdentities] = useState([]);
  const [claimsIdentities, setClaimsIdentities] = useState([]);
  const [pendingIdentities, setPendingIdentities] = useState([]);
  const [deniedIdentities, setDeniedIdentities] = useState([]);
  const [removedIdentities, setRemovedIdentities] = useState([]);
  const [underReviewIdentities, setUnderReviewIdentities] = useState([]);
  const [testerIdentities, setTesterIdentities] = useState([]);
  const [reviewerIdentities, setReviewerIdentities] = useState([]);
  const [personaResetIdentities, setPersonaResetIdentities] = useState([]);
  const [suspendedIdentities, setSuspendedIdentities] = useState([]);
  const [isLegendModalVisible, setIsLegendModalVisible] = useState(false);
  // Initialise from the URL so returning here (breadcrumb / back button) lands
  // on the originating tab instead of the default "Approved".
  const [activeTab, setActiveTab] = useState(searchParams.get("tab") || "Identities");
  const [refreshTrigger, setRefreshTrigger] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [, setPendingFilter] = useState("all");
  // Per-tab memory of the applied search/filter, keyed by getTabMeta().key, so
  // re-entering a tab restores both the controls and the matching (filtered)
  // data instead of resetting to the unfiltered list.
  const tabQueryRef = useRef({});
  const [pagination, setPagination] = useState({
    identities: { page: 1, limit: 10, totalCount: 0, hasMore: false },
    claims: { page: 1, limit: 10, totalCount: 0, hasMore: false },
    pending: { page: 1, limit: 10, totalCount: 0, hasMore: false },
    denied: { page: 1, limit: 10, totalCount: 0, hasMore: false },
    removed: { page: 1, limit: 10, totalCount: 0, hasMore: false },
    underReview: { page: 1, limit: 10, totalCount: 0, hasMore: false },
    tester: { page: 1, limit: 10, totalCount: 0, hasMore: false },
    reviewer: { page: 1, limit: 10, totalCount: 0, hasMore: false },
    personaReset: { page: 1, limit: 10, totalCount: 0, hasMore: false },
    suspended: { page: 1, limit: 10, totalCount: 0, hasMore: false },
  });
  const [loading, setLoading] = useState({
    identities: false,
    claims: false,
    pending: false,
    denied: false,
    removed: false,
    underReview: false,
    tester: false,
    reviewer: false,
    personaReset: false,
    suspended: false,
  });
  const [isAssociateModalVisible, setIsAssociateModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [associateForm] = Form.useForm();
  const { user, dfnsToken } = useContext(RoleContext);
  // adminSendVerificationEmail accepts the admin role only, unlike the page's other actions.
  const isAdmin = useIsAdmin();
  const isReviewer = useIsReviewer();

  const [investmentModalVisible, setInvestmentModalVisible] = useState(false);
  const [selectedIdentityForInvestment, setSelectedIdentityForInvestment] = useState(null);
  const [tokenProjects, setTokenProjects] = useState([]);

  // Helper function to extract error message
  const getErrorMessage = (error) => {
    if (typeof error === "string") return error;
    if (error?.reason) return error.reason;
    if (error?.message) return error.message;
    if (error?.toString) return error.toString();
    return "An unknown error occurred";
  };

  const getTabMeta = (tab) => {
    switch (tab) {
      case "Pending":
        return { key: "pending", status: "pending" };
      case "Denied":
        return { key: "denied", status: "denied" };
      case "Removed":
        return { key: "removed", status: "removed" };
      case "Under Review":
        return { key: "underReview", status: "under_review" };
      case "Testers":
        return { key: "tester", status: "tester" };
      case "Reviewers":
        return { key: "reviewer", status: "reviewer" };
      case "Persona Reset":
        return { key: "personaReset", status: "persona_reset" };
      case "Suspended":
        return { key: "suspended", status: null };
      case "Claims":
        return { key: "claims", status: null };
      default:
        return { key: "identities", status: null };
    }
  };

  const mapPendingUser = (user) => {
    const firstName = user.firstName || "";
    const lastName = user.lastName || "";
    const personaData = user.personaVerificationData ? JSON.parse(user.personaVerificationData) : {};
    const identityType = user.templateName || "";
    const name = personaData?.data?.attributes?.name || "";
    const markedForReviewAt = personaData?.data?.attributes?.payload?.data?.attributes?.["marked-for-review-at"];
    const status = markedForReviewAt
      ? "Needs Review"
      : name.split(".")[1]?.toUpperCase()?.replace(/-/g, " ") || (user.kycId ? "KYC Complete" : "") || "";

    const isReset = !!user.personaWasReset && user.personaVerified !== true;
    const displayStatus = isReset ? "Reset" : status;

    return {
      id: user.objectId,
      displayName: `${firstName} ${lastName}`.trim(),
      email: user.username || user.email || "",
      identityAddress: user.walletAddress || "",
      kyc_id: user.personaReferenceId || "",
      pepMatched: user.pepMatched || false,
      watchlistMatched: user.watchlistMatched || false,
      type: identityType,
      status: displayStatus,
      personaVerified: user.personaVerified,
      personaWasReset: user.personaWasReset || false,
      emailVerified: user.emailVerified,
      attributes: {
        firstName,
        lastName,
        email: user.email,
        username: user.username,
        walletAddress: user.walletAddress,
        personaReferenceId: user.personaReferenceId,
        personaVerificationData: user.personaVerificationData,
        pepMatched: user.pepMatched,
        watchlistMatched: user.watchlistMatched,
        kycId: user.kycId,
      },
    };
  };

  // Fetch token projects
  const fetchTokenProjects = useCallback(async () => {
    try {
      if (service && service.getTokenProjects) {
        const projects = await service.getTokenProjects();
        setTokenProjects(projects || []);
      }
    } catch (error) {
      console.error("Error fetching token projects:", error);
      toast.error("Failed to fetch token projects");
    }
  }, [service]);

  // Fetch token projects on component mount
  useEffect(() => {
    void fetchTokenProjects();
  }, [fetchTokenProjects]);

  const fetchData = useCallback(
    async (tab, page = 1, search = "", filter = "all") => {
      const { key, status } = getTabMeta(tab);
      setLoading((prev) => ({ ...prev, [key]: true }));

      try {
        let fetchedIdentities = [];
        let paginationData = null;

        if (tab === "Identities") {
          // Call paginated cloud function for active identities
          const result = await Parse.Cloud.run("getActiveIdentities", {
            page,
            limit: pagination.identities.limit,
            withoutClaims: false, // Get all identities
            searchTerm: search,
          });

          if (result && result.data) {
            fetchedIdentities = result.data.map((identity) => {
              const identityObj = {
                claims: identity.claims || "",
                displayName: identity.displayName || "",
                email: identity.email || "",
                kyc_id: identity.kyc_id || "",
                identityAddress: identity.identityAddress || "",
                id: identity.id || "",
                pepMatched: identity.pepMatched || false,
                watchlistMatched: identity.watchlistMatched || false,
                walletAddress: identity.walletAddress || "",
              };

              // Re-sort claims numerically on the client side
              if (identityObj.claims) {
                const claimsArray = identityObj.claims.split(", ");
                const sortedClaims = claimsArray
                  .map((claim) => parseInt(claim, 10))
                  .filter((claim) => !isNaN(claim))
                  .sort((a, b) => a - b)
                  .map((claim) => claim.toString());

                identityObj.claims = sortedClaims.join(", ");
              }

              return identityObj;
            });

            paginationData = result.pagination;
          }

          setIdentities(fetchedIdentities);
          setPagination((prev) => ({
            ...prev,
            identities: { ...prev.identities, ...paginationData, page },
          }));
        } else if (tab === "Suspended") {
          // Same cloud function as Approved, scoped to suspended users only.
          const result = await Parse.Cloud.run("getActiveIdentities", {
            page,
            limit: pagination.suspended.limit,
            withoutClaims: false,
            searchTerm: search,
            onlySuspended: true,
          });

          if (result && result.data) {
            fetchedIdentities = result.data.map((identity) => ({
              claims: identity.claims || "",
              displayName: identity.displayName || "",
              email: identity.email || "",
              kyc_id: identity.kyc_id || "",
              identityAddress: identity.identityAddress || "",
              id: identity.id || "",
              pepMatched: identity.pepMatched || false,
              watchlistMatched: identity.watchlistMatched || false,
              walletAddress: identity.walletAddress || "",
            }));

            paginationData = result.pagination;
          }

          setSuspendedIdentities(fetchedIdentities);
          setPagination((prev) => ({
            ...prev,
            suspended: { ...prev.suspended, ...paginationData, page },
          }));
        } else if (tab === "Claims") {
          // Call paginated cloud function for identities without claims
          const result = await Parse.Cloud.run("getActiveIdentities", {
            page,
            limit: pagination.claims.limit,
            withoutClaims: true, // Get only identities without claims
            searchTerm: search,
          });

          if (result?.data) {
            fetchedIdentities = result.data.map((identity) => ({
              claims: identity.claims || "",
              displayName: identity.displayName || "",
              email: identity.email || "",
              kyc_id: identity.kyc_id || "",
              identityAddress: identity.identityAddress || "",
              id: identity.id || "",
              pepMatched: identity.pepMatched || false,
              watchlistMatched: identity.watchlistMatched || false,
            }));

            paginationData = result.pagination;
          }

          setClaimsIdentities(fetchedIdentities);
          setPagination((prev) => ({
            ...prev,
            claims: { ...prev.claims, ...paginationData, page },
          }));
        } else if (
          tab === "Pending" ||
          tab === "Denied" ||
          tab === "Removed" ||
          tab === "Under Review" ||
          tab === "Testers" ||
          tab === "Reviewers" ||
          tab === "Persona Reset"
        ) {
          // Call paginated cloud function for pending identities with filter
          const result = await Parse.Cloud.run("getPendingIdentities", {
            page,
            limit: pagination[key].limit,
            searchTerm: search,
            filter: tab === "Pending" ? filter : "all",
            status,
          });

          if (result?.data) {
            fetchedIdentities = result.data.map(mapPendingUser);

            if (tab === "Persona Reset") {
              fetchedIdentities = fetchedIdentities.map((identity) => ({
                ...identity,
                status: "Reset",
              }));
            }

            paginationData = result.pagination;
          }

          if (tab === "Pending") {
            setPendingIdentities(fetchedIdentities);
            setPagination((prev) => ({
              ...prev,
              pending: { ...prev.pending, ...paginationData, page },
            }));
          } else if (tab === "Denied") {
            setDeniedIdentities(fetchedIdentities);
            setPagination((prev) => ({
              ...prev,
              denied: { ...prev.denied, ...paginationData, page },
            }));
          } else if (tab === "Removed") {
            setRemovedIdentities(fetchedIdentities);
            setPagination((prev) => ({
              ...prev,
              removed: { ...prev.removed, ...paginationData, page },
            }));
          } else if (tab === "Under Review") {
            setUnderReviewIdentities(fetchedIdentities);
            setPagination((prev) => ({
              ...prev,
              underReview: { ...prev.underReview, ...paginationData, page },
            }));
          } else if (tab === "Testers") {
            setTesterIdentities(fetchedIdentities);
            setPagination((prev) => ({
              ...prev,
              tester: { ...prev.tester, ...paginationData, page },
            }));
          } else if (tab === "Persona Reset") {
            setPersonaResetIdentities(fetchedIdentities);
            setPagination((prev) => ({
              ...prev,
              personaReset: { ...prev.personaReset, ...paginationData, page },
            }));
          } else {
            setReviewerIdentities(fetchedIdentities);
            setPagination((prev) => ({
              ...prev,
              reviewer: { ...prev.reviewer, ...paginationData, page },
            }));
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error(`Failed to fetch data: ${getErrorMessage(error)}`);
      } finally {
        // Clear loading state for specific tab
        setLoading((prev) => ({ ...prev, [key]: false }));
      }
    },
    // Deliberately narrowed to each tab's `.limit` only: this callback itself updates
    // `pagination.page`/`.totalCount` via setPagination, so depending on the whole
    // `pagination` object would re-create it (and re-run effects keyed on it) every fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      pagination.identities.limit,
      pagination.claims.limit,
      pagination.pending.limit,
      pagination.denied.limit,
      pagination.removed.limit,
      pagination.underReview.limit,
      pagination.tester.limit,
      pagination.reviewer.limit,
      pagination.personaReset.limit,
      pagination.suspended.limit,
    ]
  );

  // Keep the active tab in sync with the URL `tab` param. Needed when a global
  // search result navigates within the Identities page (no remount), e.g. from
  // the Approved tab to `?tab=Pending&q=...` — otherwise the tab wouldn't change.
  useEffect(() => {
    const urlTab = searchParams.get("tab");
    if (urlTab && urlTab !== activeTab) {
      setActiveTab(urlTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    // Reset pagination to page 1. A deep-link `q` param takes precedence over
    // the per-tab remembered search so global-search navigation lands filtered.

    // If the URL points at a different tab than the current state, defer: the
    // tab-sync effect will update activeTab and this effect will re-run with the
    // correct tab (prevents fetching/remembering the wrong tab's search).
    const urlTab = searchParams.get("tab");
    if (urlTab && urlTab !== activeTab) {
      return;
    }

    const { key } = getTabMeta(activeTab);
    const remembered = tabQueryRef.current[key] || {};
    const urlSearch = searchParams.get("q") || "";
    const effectiveSearch = urlSearch || remembered.search || "";

    // Persist the effective term so pagination clicks keep filtering by it.
    tabQueryRef.current[key] = { ...tabQueryRef.current[key], search: effectiveSearch };

    setPagination((prev) => ({
      ...prev,
      [key]: { ...prev[key], page: 1 },
    }));

    if (activeTab !== "Pending") {
      setSearchTerm(effectiveSearch);
      void fetchData(activeTab, 1, effectiveSearch);
    } else {
      const rememberedFilter = remembered.filter || "all";
      setSearchTerm(effectiveSearch);
      setPendingFilter(rememberedFilter);
      void fetchData(activeTab, 1, effectiveSearch, rememberedFilter);
    }
    // fetchData intentionally omitted: only re-run on tab/refresh/URL changes (see the
    // matching eslint-disable above fetchData's own useCallback deps for why its identity
    // shouldn't drive this effect).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, refreshTrigger, searchParams]);

  const removeFromLocalState = (record) => {
    const { id } = record;
    setIdentities((prev) => prev.filter((item) => item.id !== id));
    setClaimsIdentities((prev) => prev.filter((item) => item.id !== id));
    setPendingIdentities((prev) => prev.filter((item) => item.id !== id));
    setDeniedIdentities((prev) => prev.filter((item) => item.id !== id));
    setRemovedIdentities((prev) => prev.filter((item) => item.id !== id));
    setUnderReviewIdentities((prev) => prev.filter((item) => item.id !== id));
    setTesterIdentities((prev) => prev.filter((item) => item.id !== id));
    setReviewerIdentities((prev) => prev.filter((item) => item.id !== id));
    setSuspendedIdentities((prev) => prev.filter((item) => item.id !== id));
  };

  const handleTabChange = (key) => {
    setActiveTab(key);
    // Reflect the active tab in the URL so it survives refresh and so detail
    // pages can route their breadcrumb / back button to the originating tab.
    setSearchParams({ tab: key }, { replace: true });
  };

  const handlePageChange = (newPage, newSearchTerm) => {
    const { key } = getTabMeta(activeTab);
    setPagination((prev) => ({ ...prev, [key]: { ...prev[key], page: newPage } }));
    // PendingIdentitiesObjectList passes a search term as the second arg when
    // "View Results" is pressed on tabs that don't have onFilterChange wired
    // (Denied, Removed, Under Review, Testers). Persist it so subsequent
    // pagination clicks (which omit the arg) keep filtering by the same term.
    const effectiveSearch = newSearchTerm !== undefined ? newSearchTerm : searchTerm;
    if (newSearchTerm !== undefined) {
      setSearchTerm(newSearchTerm);
      tabQueryRef.current[key] = { ...tabQueryRef.current[key], search: newSearchTerm };
    }
    void fetchData(activeTab, newPage, effectiveSearch);
  };

  const handleSearch = (newSearchTerm) => {
    const { key } = getTabMeta(activeTab);
    setPagination((prev) => ({ ...prev, [key]: { ...prev[key], page: 1 } }));
    setSearchTerm(newSearchTerm);
    tabQueryRef.current[key] = { ...tabQueryRef.current[key], search: newSearchTerm };
    void fetchData(activeTab, 1, newSearchTerm);
  };

  const handlePendingFilterChange = (searchTerm, filter, page = 1) => {
    setPagination((prev) => ({ ...prev, pending: { ...prev.pending, page } }));
    setSearchTerm(searchTerm);
    setPendingFilter(filter);
    tabQueryRef.current.pending = { search: searchTerm, filter };
    void fetchData("Pending", page, searchTerm, filter);
  };

  const handleDenyUser = async (record) => {
    const { id, displayName } = record;
    removeFromLocalState(record);
    toast
      .promise(
        async () => {
          const result = await Parse.Cloud.run("setUserStatus", { userId: id, status: "Denied" });
          return result?.success;
        },
        {
          pending: `Denying ${displayName}...`,
          success: (deleted) => {
            if (deleted) {
              return `${displayName} has been successfully denied.`;
            } else {
              throw new Error(`${displayName} couldn't be denied.`);
            }
          },
          error: {
            render({ data }) {
              const errorMessage = getErrorMessage(data);
              return `${displayName} couldn't be denied: ${errorMessage}`;
            },
          },
        }
      )
      .then(() => {
        setRefreshTrigger((prev) => !prev);
      })
      .catch((error) => {
        console.error("Error in handleDenyUser:", error);
      });
  };

  const handleSendVerificationEmail = async (record) => {
    const { displayName } = record;
    // Pending-tab rows show `username || email` as the email; the backend looks users up by email.
    const email = record.attributes?.email || record.email;

    if (!email) {
      toast.error("Email address is missing for this user");
      return;
    }

    const toastId = toast.loading(`Sending verification link to ${displayName || email}...`);
    try {
      // Admin-only: unlike the public sendVerificationEmail, it reports whether a link really went out.
      const result = await Parse.Cloud.run("adminSendVerificationEmail", { email });
      // sent is false when the email is already verified; show that as info, not as a green "sent".
      toast.update(toastId, {
        render: result?.message || (result?.sent ? "Verification link sent." : "No link was sent."),
        type: result?.sent ? "success" : "info",
        isLoading: false,
        autoClose: 5000,
      });
    } catch (error) {
      console.error("Error sending verification email:", error);
      toast.update(toastId, {
        render: `Failed to send verification link: ${getErrorMessage(error)}`,
        type: "error",
        isLoading: false,
        autoClose: 5000,
      });
    }
  };

  // ─── Reset Persona Verification ─────────────────────────────────────────
  const handleResetPersonaVerification = async (record) => {
    const { id, displayName } = record;

    toast
      .promise(
        async () => {
          const result = await Parse.Cloud.run("resetPersonaVerification", {
            userId: id,
          });
          return result;
        },
        {
          pending: `Resetting Persona verification for ${displayName}...`,
          success: `Persona verification reset for ${displayName}.`,
          error: {
            render({ data }) {
              return `Failed to reset Persona verification: ${getErrorMessage(data)}`;
            },
          },
        }
      )
      .then(() => setRefreshTrigger((prev) => !prev))
      .catch((error) => console.error("Error in handleResetPersonaVerification:", error));
  };

  const handleRemoveIdentity = async (event, action, record) => {
    const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
    const identities = [record.identityAddress];
    removeFromLocalState(record);
    for (const identity of identities) {
      try {
        await toast.promise(
          (async () => {
            try {
              // Deregister from the identity registry FIRST.
              //
              // This is the only step that emits IdentityRemoved, which is what
              // flips Identity.active to false in Parse (see the IdentityRemoved
              // case in src/triggers/Event.ts). Previously the factory removal ran
              // first, and when it reverted the whole chain aborted before ever
              // reaching this call — so the identity stayed active in the admin
              // list. Worse, the factory reverts permanently once its mapping is
              // cleared, meaning retries could never reach the registry either.
              const { initiateResponse: unregisterInitResponse, error: unregisterInitError } = await DfnsService.initiateUnregisterIdentity(
                identity,
                user.walletId,
                dfnsToken
              );
              if (unregisterInitError) throw new Error(unregisterInitError);

              const { error: unregisterCompleteError } = await DfnsService.completeUnregisterIdentity(
                user.walletId,
                dfnsToken,
                unregisterInitResponse.challenge,
                unregisterInitResponse.requestBody
              );
              if (unregisterCompleteError) throw new Error(unregisterCompleteError);

              await delay(2000);

              // Then clear the factory's clone mapping — best effort.
              //
              // The identity is already deregistered by this point, so a failure
              // here must not fail the operation. The factory reverts with
              // "Identity does not exist" whenever its mapping was already cleared
              // by an earlier attempt, which is a no-op, not an error.
              try {
                const { initiateResponse, error: removeInitError } = await DfnsService.initiateRemoveIdentity(identity, user.walletId, dfnsToken);
                if (removeInitError) throw new Error(removeInitError);

                const { error: removeCompleteError } = await DfnsService.completeRemoveIdentity(
                  user.walletId,
                  dfnsToken,
                  initiateResponse.challenge,
                  initiateResponse.requestBody
                );
                if (removeCompleteError) throw new Error(removeCompleteError);
              } catch (factoryError) {
                console.warn(`Identity ${identity} deregistered, but factory cleanup failed (non-fatal):`, getErrorMessage(factoryError));
              }

              await delay(2500);
              setRefreshTrigger((prev) => !prev);
            } catch (error) {
              console.error("Error in remove identity operation:", error);
              throw error;
            }
          })(),
          {
            pending: `Removing ${record?.displayName}...`,
            success: `Successfully removed ${record?.displayName}`,
            error: {
              render({ data }) {
                return <div>{getErrorMessage(data) || `An error occurred while removing ${record?.displayName}`}</div>;
              },
            },
          }
        );
      } catch (error) {
        console.error(`Error removing identity ${identity}:`, error);
      }
    }
  };

  // ─── Move user between pending sub-states via setUserStatus ──────────────
  const handleSetUserStatus = async (record, status) => {
    const { id, displayName } = record;
    toast
      .promise(async () => Parse.Cloud.run("setUserStatus", { userId: id, status }), {
        pending: `Moving ${displayName} to ${status}...`,
        success: `${displayName} moved to ${status}.`,
        error: {
          render({ data }) {
            return `Failed to move ${displayName} to ${status}: ${getErrorMessage(data)}`;
          },
        },
      })
      .then(() => {
        removeFromLocalState(record);
        setRefreshTrigger((prev) => !prev);
      })
      .catch((error) => console.error("Error in handleSetUserStatus:", error));
  };

  // ─── Revoke a user's Reviewer role (role overlay; no blockchain) ──────────
  const handleRevokeReviewer = async (record) => {
    const { id, displayName } = record;
    toast
      .promise(async () => Parse.Cloud.run("revokeReviewerRole", { userId: id }), {
        pending: `Revoking reviewer role for ${displayName}...`,
        success: `${displayName} is no longer a reviewer.`,
        error: {
          render({ data }) {
            return `Failed to revoke reviewer role for ${displayName}: ${getErrorMessage(data)}`;
          },
        },
      })
      .then(() => {
        removeFromLocalState(record);
        setRefreshTrigger((prev) => !prev);
      })
      .catch((error) => console.error("Error in handleRevokeReviewer:", error));
  };

  // ─── Suspend / unsuspend a user (compliance hold; no on-chain action) ────
  const handleToggleSuspension = async (record, suspended) => {
    const { displayName, walletAddress } = record;
    removeFromLocalState(record);
    toast
      .promise(async () => Parse.Cloud.run("setUserSuspension", { walletAddress, suspended }), {
        pending: suspended ? `Suspending ${displayName}...` : `Unsuspending ${displayName}...`,
        success: suspended ? `${displayName} has been suspended.` : `${displayName} has been unsuspended.`,
        error: {
          render({ data }) {
            const errorMessage = getErrorMessage(data);
            return suspended ? `Failed to suspend ${displayName}: ${errorMessage}` : `Failed to unsuspend ${displayName}: ${errorMessage}`;
          },
        },
      })
      .then(() => setRefreshTrigger((prev) => !prev))
      .catch((error) => console.error("Error in handleToggleSuspension:", error));
  };

  // ─── Promote a user to Reviewer (read-only role) ─────────────────────────
  const handleSetUserAsReviewer = async (record) => {
    const { id, displayName, identityAddress } = record;
    // Approved identities carry an Identity object id in `id`, so we promote by
    // wallet address. Pending users carry their Parse _User id, which works even
    // before a wallet exists.
    const params = activeTab === "Identities" ? { walletAddress: identityAddress } : { userId: id };

    toast
      .promise(async () => Parse.Cloud.run("setUserAsReviewer", params), {
        pending: `Making ${displayName} a reviewer...`,
        success: `${displayName} is now a reviewer and can log in to the admin UI to view all pages with read-only access.`,
        error: {
          render({ data }) {
            return `Failed to make ${displayName} a reviewer: ${getErrorMessage(data)}`;
          },
        },
      })
      .then(() => {
        // The reviewer role no longer changes the user's userStatus, so it
        // coexists with whatever tab they're in (Approved/Pending/Under
        // Review/Tester) AND adds them to Reviewers. We therefore never remove
        // the row from its current tab — just refresh so Reviewers picks it up.
        setRefreshTrigger((prev) => !prev);
      })
      .catch((error) => console.error("Error in handleSetUserAsReviewer:", error));
  };

  const handleAssociateInquiry = (record) => {
    setSelectedRecord(record);
    setIsAssociateModalVisible(true);
  };

  const handleAssociateModalCancel = () => {
    setIsAssociateModalVisible(false);
    setSelectedRecord(null);
    associateForm.resetFields();
  };

  const handleAssociateModalOk = async () => {
    if (!selectedRecord) return;
    try {
      const values = await associateForm.validateFields();
      const { personaInquiryId } = values;

      await toast.promise(
        async () => {
          const result = await Parse.Cloud.run("associatePersonaInquiry", {
            userId: selectedRecord.id,
            personaInquiryId,
          });
          return result;
        },
        {
          pending: `Associating Persona Inquiry ID for ${selectedRecord.displayName}...`,
          success: `Successfully associated Persona Inquiry ID for ${selectedRecord.displayName}`,
          error: {
            render({ data }) {
              return `Failed to associate Persona Inquiry ID: ${getErrorMessage(data)}`;
            },
          },
        }
      );

      handleAssociateModalCancel();
      setRefreshTrigger((prev) => !prev);
    } catch (error) {
      console.error("Error in handleAssociateModalOk:", error);
    }
  };

  const handleAction = async (event, action, record) => {
    try {
      switch (action) {
        case NomyxAction.CreateIdentity:
          navigate(`/identities/create?from=${encodeURIComponent(activeTab)}`);
          break;
        case NomyxAction.ViewIdentity:
          navigate(`/identities/${record.id}?email=${encodeURIComponent(record.email || "")}&from=${encodeURIComponent(activeTab)}`);
          break;
        case NomyxAction.ViewPendingIdentity:
          // Pass the already-loaded list record via router state. The detail
          // view's own client-side _User query (pendingApproval=true &&
          // denied=false) is narrower than the list's cloud function and can
          // miss users in other statuses (Denied/Under Review/Tester/Reviewer),
          // causing it to bounce back to /identities with an empty page.
          navigate(`/identities/pending/${record.id}?email=${encodeURIComponent(record.email || "")}&from=${encodeURIComponent(activeTab)}`, {
            state: { record },
          });
          break;
        case NomyxAction.EditClaims:
          navigate(`/identities/${record.id}/edit?email=${encodeURIComponent(record.email || "")}&from=${encodeURIComponent(activeTab)}`);
          break;
        case NomyxAction.AddClaims:
          navigate(`/identities/${record.id}/edit?email=${encodeURIComponent(record.email || "")}&from=${encodeURIComponent(activeTab)}`);
          break;
        case NomyxAction.RemoveIdentity:
          await handleRemoveIdentity(event, action, record);
          break;
        case NomyxAction.RequestInvestment:
          setSelectedIdentityForInvestment(record);
          setInvestmentModalVisible(true);
          break;
        case NomyxAction.CreatePendingIdentity:
          const { displayName, kyc_id, identityAddress, id, email } = record;
          navigate(
            `/identities/create?displayName=${encodeURIComponent(displayName)}&walletAddress=${encodeURIComponent(identityAddress)}&accountNumber=${encodeURIComponent(kyc_id)}&userId=${encodeURIComponent(id || "")}&email=${encodeURIComponent(email || "")}&from=${encodeURIComponent(activeTab)}`
          );
          break;
        case NomyxAction.AssociateInquiry:
          handleAssociateInquiry(record);
          break;
        case NomyxAction.SendVerificationEmail:
          await handleSendVerificationEmail(record);
          break;
        case NomyxAction.DenyUser:
          await handleDenyUser(record);
          break;
        case NomyxAction.MoveToUnderReview:
          await handleSetUserStatus(record, "Under Review");
          break;
        case NomyxAction.MoveToTester:
          await handleSetUserStatus(record, "Tester");
          break;
        case NomyxAction.MoveToReviewer:
          await handleSetUserAsReviewer(record);
          break;
        case NomyxAction.MoveToPending:
          await handleSetUserStatus(record, "Pending");
          break;
        case NomyxAction.RevokeReviewer:
          await handleRevokeReviewer(record);
          break;
        case NomyxAction.SuspendIdentity:
          await handleToggleSuspension(record, true);
          break;
        case NomyxAction.UnsuspendIdentity:
          await handleToggleSuspension(record, false);
          break;
        case NomyxAction.ResetPersonaVerification:
          await handleResetPersonaVerification(record);
          break;
        default:
          console.log("Action not handled: ", action);
          break;
      }
    } catch (error) {
      console.error("Error in handleAction:", error);
      toast.error(`Action failed: ${getErrorMessage(error)}`);
    }
  };

  // ─── Current Tab Data ─────────────────────────────────────────────────────────

  const getCurrentData = () => {
    switch (activeTab) {
      case "Identities":
        return identities;
      case "Claims":
        return claimsIdentities;
      case "Pending":
        return pendingIdentities;
      case "Denied":
        return deniedIdentities;
      case "Removed":
        return removedIdentities;
      case "Under Review":
        return underReviewIdentities;
      case "Testers":
        return testerIdentities;
      case "Reviewers":
        return reviewerIdentities;
      case "Persona Reset":
        return personaResetIdentities;
      case "Suspended":
        return suspendedIdentities;
      default:
        return [];
    }
  };

  // Opens the Persona inquiry dashboard for this identity's KYC inquiry in a
  // new tab, so an admin can inspect it directly on Persona's side.
  //
  // Two different table components use this column, with different `render`
  // calling conventions: ObjectList.jsx calls render(record), while
  // PendingIdentitiesObjectList.jsx calls render(record[column.name], record)
  // — i.e. the field's value first, full record second. Handle both so this
  // works regardless of which table renders it.
  const renderKycInquiryIdLink = (valueOrRecord, record) => {
    const inquiryId = typeof valueOrRecord === "string" ? valueOrRecord : valueOrRecord?.kyc_id || record?.kyc_id;
    if (!inquiryId) return null;

    const baseUrl = process.env.REACT_APP_PERSONA_DASHBOARD_BASE_URL || "";
    return (
      <a href={`${baseUrl}${inquiryId}`} target="_blank" rel="noopener noreferrer">
        {inquiryId}
      </a>
    );
  };

  const renderStatusBadge = (value) => {
    const label = (value || "").toString().trim();
    if (!label) return <span style={{ color: "var(--text-secondary, #9ca3af)" }}> </span>;

    const key = label.toUpperCase();
    const palette = {
      "NEEDS REVIEW": { bg: "#FEF3C7", fg: "#92400E", dot: "#D97706" },
      "MARKED FOR REVIEW": { bg: "#FEF3C7", fg: "#92400E", dot: "#D97706" },
      APPROVED: { bg: "#DCFCE7", fg: "#166534", dot: "#16A34A" },
      COMPLETED: { bg: "#DBEAFE", fg: "#1E40AF", dot: "#2563EB" },
      "KYC COMPLETE": { bg: "#CCFBF1", fg: "#115E59", dot: "#0D9488" },
      FAILED: { bg: "#FEE2E2", fg: "#991B1B", dot: "#DC2626" },
      DECLINED: { bg: "#FEE2E2", fg: "#991B1B", dot: "#DC2626" },
      PENDING: { bg: "#F3F4F6", fg: "#374151", dot: "#6B7280" },
      RESET: { bg: "#FEF3C7", fg: "#92400E", dot: "#D97706" },
    };
    const c = palette[key] || { bg: "#F3F4F6", fg: "#374151", dot: "#6B7280" };

    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "0.4rem",
          padding: "0.15rem 0.6rem",
          borderRadius: "9999px",
          backgroundColor: c.bg,
          color: c.fg,
          fontSize: "0.75rem",
          fontWeight: 600,
          lineHeight: 1.6,
          whiteSpace: "nowrap",
          textTransform: "capitalize",
        }}
      >
        <span
          style={{
            width: "0.5rem",
            height: "0.5rem",
            borderRadius: "9999px",
            backgroundColor: c.dot,
            flexShrink: 0,
          }}
        />
        {label.toLowerCase()}
      </span>
    );
  };

  // ─── Column Definitions ───────────────────────────────────────────────────────

  const columns = [
    { label: "Identity", name: "displayName" },
    { label: "Email", name: "email" },
    { label: "Address", name: "identityAddress", width: "350px" },
    { label: "KYC Inquiry Id", name: "kyc_id", render: renderKycInquiryIdLink },
    { label: "Flagged?", name: "flagged_account" },
    { label: "Claims", name: "claims" },
  ];

  const pendingColumns = [
    { label: "Identity", name: "displayName" },
    { label: "Email", name: "email" },
    { label: "Address", name: "identityAddress", width: "350px" },
    { label: "KYC Inquiry Id", name: "kyc_id", render: renderKycInquiryIdLink },
    { label: "Flagged?", name: "flagged_account" },
    { label: "Type", name: "type", width: "180px" },
    { label: "Status", name: "status", render: renderStatusBadge },
  ];

  const addRulesColumns = [
    { label: "Identity", name: "displayName" },
    { label: "Email", name: "email" },
    { label: "Address", name: "identityAddress", width: "350px" },
    { label: "KYC Inquiry Id", name: "kyc_id", render: renderKycInquiryIdLink },
    { label: "Flagged?", name: "flagged_account" },
  ];

  const actions = [
    { label: "Edit", name: NomyxAction.EditClaims, icon: <EditOutlined /> },
    { label: "View", name: NomyxAction.ViewIdentity, icon: <EyeOutlined /> },
    { label: "Request for Investment", name: NomyxAction.RequestInvestment, icon: <DollarOutlined /> },
    {
      label: "Grant Reviewer Role",
      name: NomyxAction.MoveToReviewer,
      icon: <AuditOutlined />,
      confirmation: "Make this user a reviewer? They will get read-only access to all pages.",
    },
    {
      label: "Suspend",
      name: NomyxAction.SuspendIdentity,
      icon: <StopOutlined />,
      confirmation: "Suspend this user? They will be blocked from new investment requests until unsuspended.",
    },
    {
      label: "Remove",
      name: NomyxAction.RemoveIdentity,
      icon: <DeleteOutlined />,
      confirmation: "Are you sure you want to remove this Identity?",
    },
  ];

  const suspendedActions = [
    { label: "View", name: NomyxAction.ViewIdentity, icon: <EyeOutlined /> },
    { label: "Edit", name: NomyxAction.EditClaims, icon: <EditOutlined /> },
    {
      label: "Unsuspend",
      name: NomyxAction.UnsuspendIdentity,
      icon: <UndoOutlined />,
      confirmation: "Unsuspend this user? They will be able to submit investment requests again.",
    },
  ];

  // GM-2436: no verification link to send once the email is verified.
  const sendVerificationLinkAction = {
    label: "Send Verification Link",
    name: NomyxAction.SendVerificationEmail,
    icon: <MailOutlined />,
    disabled: (r) => r.emailVerified === true,
    disabledTooltip: "Email already verified",
  };
  const sendVerificationActions = isAdmin ? [sendVerificationLinkAction] : [];

  const pendingActions = [
    { label: "Approve", name: NomyxAction.CreatePendingIdentity, icon: <CheckOutlined /> },
    { label: "View", name: NomyxAction.ViewPendingIdentity, icon: <EyeOutlined /> },
    { label: "Associate", name: NomyxAction.AssociateInquiry, icon: <LinkOutlined /> },
    ...sendVerificationActions,
    {
      label: "Reset Persona",
      name: NomyxAction.ResetPersonaVerification,
      icon: <ReloadOutlined />,
      disabled: (r) => !!r.personaWasReset && r.personaVerified !== true,
      confirmation: "This will clear the user's current Persona verification and require them to re-verify their identity. Are you sure?",
    },
    {
      label: "Move to Under Review",
      name: NomyxAction.MoveToUnderReview,
      icon: <FileSearchOutlined />,
      confirmation: "Move this user to Under Review?",
    },
    {
      label: "Move to Tester",
      name: NomyxAction.MoveToTester,
      icon: <ExperimentOutlined />,
      confirmation: "Move this user to Tester?",
    },
    {
      label: "Grant Reviewer Role",
      name: NomyxAction.MoveToReviewer,
      icon: <AuditOutlined />,
      confirmation: "Make this user a reviewer? They will get read-only access to all pages.",
    },
    {
      label: "Deny",
      name: NomyxAction.DenyUser,
      icon: <CloseOutlined />,
      confirmation: "Are you sure you want to deny this pending Identity?",
    },
  ];

  const reviewerActions = [
    { label: "View", name: NomyxAction.ViewPendingIdentity, icon: <EyeOutlined /> },
    {
      label: "Revoke Reviewer role",
      name: NomyxAction.RevokeReviewer,
      icon: <DeleteOutlined />,
      confirmation: "Revoke this user's reviewer role? They will lose read-only admin access and leave the Reviewers tab.",
    },
  ];

  // Workflow states an identity can be moved between. Reviewer is intentionally
  // NOT here: it is a coexisting role overlay granted from Pending/Approved, not
  // a move target. `tab` values match the Tabs `key`s below.
  const MOVE_DESTINATIONS = [
    { tab: "Pending", action: NomyxAction.MoveToPending, label: "Pending", confirmation: "Move this user back to Pending?" },
    { tab: "Under Review", action: NomyxAction.MoveToUnderReview, label: "Under Review", confirmation: "Move this user to Under Review?" },
    { tab: "Testers", action: NomyxAction.MoveToTester, label: "Tester", confirmation: "Move this user to Tester?" },
    { tab: "Denied", action: NomyxAction.DenyUser, label: "Denied", confirmation: "Move this user to Denied?" },
  ];

  // Build the single "Move to" dropdown action for a given tab, excluding the
  // tab itself from the destination list.
  const makeMoveDropdown = (currentTab) => ({
    type: "dropdown",
    name: "MoveTo",
    label: "Move to",
    icon: <SwapOutlined />,
    items: MOVE_DESTINATIONS.filter((d) => d.tab !== currentTab).map((d) => ({
      label: d.label,
      name: d.action,
      confirmation: d.confirmation,
    })),
  });

  const subStateActions = [
    { label: "Approve", name: NomyxAction.CreatePendingIdentity, icon: <CheckOutlined /> },
    { label: "View", name: NomyxAction.ViewPendingIdentity, icon: <EyeOutlined /> },
    { label: "Associate", name: NomyxAction.AssociateInquiry, icon: <LinkOutlined /> },
    ...sendVerificationActions,
    {
      label: "Reset Persona",
      name: NomyxAction.ResetPersonaVerification,
      icon: <ReloadOutlined />,
      disabled: (r) => !!r.personaWasReset && r.personaVerified !== true,
      confirmation: "This will clear the user's current Persona verification and require them to re-verify their identity. Are you sure?",
    },
    makeMoveDropdown("Testers"),
  ];

  const actionLegend = [
    { icon: <EditOutlined />, label: "Edit Rules", description: "Edit the compliance rule assignments for this identity" },
    { icon: <EyeOutlined />, label: "View", description: "Open the identity's detail view" },
    { icon: <DollarOutlined />, label: "Request for Investment", description: "Request an investment from this identity" },
    { icon: <StopOutlined />, label: "Suspend", description: "Place a compliance hold on this user, blocking new investment requests" },
    { icon: <UndoOutlined />, label: "Unsuspend", description: "Lift a compliance hold, restoring this user to the Approved tab" },
    { icon: <DeleteOutlined />, label: "Remove", description: "Remove this identity from the blockchain" },
    { icon: <CheckOutlined />, label: "Approve", description: "Approve a pending identity and create their on-chain record" },
    { icon: <LinkOutlined />, label: "Associate", description: "Associate a Persona Inquiry ID with this user" },
    {
      icon: <MailOutlined />,
      label: "Send Verification Link",
      description: "Email this user a link to verify their email address (valid 24 hours; replaces any earlier verification or onboarding link)",
    },
    { icon: <ReloadOutlined />, label: "Reset Persona", description: "Reset the user's Persona verification (re-verify required)" },
    { icon: <FileSearchOutlined />, label: "Move to Under Review", description: "Move this pending user to Under Review status" },
    { icon: <ExperimentOutlined />, label: "Move to Tester", description: "Move this pending user to Tester status" },
    { icon: <AuditOutlined />, label: "Grant Reviewer Role", description: "Grant this user the read-only reviewer role" },
    {
      icon: <SwapOutlined />,
      label: "Move to",
      description: "Move this identity to another workflow state (Pending, Under Review, Tester, or Denied)",
    },
    {
      icon: <DeleteOutlined />,
      label: "Revoke Reviewer role",
      description: "Remove the reviewer role; the identity leaves the Reviewers tab and stays in its base tab",
    },
    { icon: <CloseOutlined />, label: "Deny", description: "Deny this pending identity" },
    { icon: <PlusOutlined />, label: "Add Rules", description: "Add compliance rules to this identity" },
  ];

  // Reviewers only have read-only "View" access, so the legend shows just that.
  const visibleLegend = isReviewer
    ? actionLegend.filter((item) => item.label === "View")
    : actionLegend.filter((item) => isAdmin || item.label !== "Send Verification Link");

  const underReviewActions = [
    { label: "Approve", name: NomyxAction.CreatePendingIdentity, icon: <CheckOutlined /> },
    { label: "View", name: NomyxAction.ViewPendingIdentity, icon: <EyeOutlined /> },
    { label: "Associate", name: NomyxAction.AssociateInquiry, icon: <LinkOutlined /> },
    ...sendVerificationActions,
    {
      label: "Reset Persona",
      name: NomyxAction.ResetPersonaVerification,
      icon: <ReloadOutlined />,
      disabled: (r) => !!r.personaWasReset && r.personaVerified !== true,
      confirmation: "This will clear the user's current Persona verification and require them to re-verify their identity. Are you sure?",
    },
    makeMoveDropdown("Under Review"),
  ];

  const personaResetActions = [{ label: "View", name: NomyxAction.ViewPendingIdentity, icon: <EyeOutlined /> }, ...sendVerificationActions];

  const deniedActions = [
    { label: "Approve", name: NomyxAction.CreatePendingIdentity, icon: <CheckOutlined /> },
    { label: "View", name: NomyxAction.ViewPendingIdentity, icon: <EyeOutlined /> },
    { label: "Associate", name: NomyxAction.AssociateInquiry, icon: <LinkOutlined /> },
    ...sendVerificationActions,
    makeMoveDropdown("Denied"),
  ];

  const removedActions = [
    { label: "Re-approve", name: NomyxAction.CreatePendingIdentity, icon: <CheckOutlined /> },
    { label: "View", name: NomyxAction.ViewPendingIdentity, icon: <EyeOutlined /> },
    { label: "Associate", name: NomyxAction.AssociateInquiry, icon: <LinkOutlined /> },
    ...sendVerificationActions,
  ];

  const claimsActions = [
    { label: "Add Rules", name: NomyxAction.AddClaims, icon: <PlusOutlined /> },
    { label: "View", name: NomyxAction.ViewIdentity, icon: <EyeOutlined /> },
    {
      label: "Remove",
      name: NomyxAction.RemoveIdentity,
      icon: <DeleteOutlined />,
      confirmation: "Are you sure you want to remove this Identity?",
    },
  ];

  const globalActions = [{ label: "Create identity", name: NomyxAction.CreateIdentity, icon: <PlusOutlined /> }];

  const search = true;

  return (
    <>
      <Tabs
        activeKey={activeTab}
        onChange={handleTabChange}
        tabBarExtraContent={
          <Button className="!bg-green-600" icon={<QuestionCircleOutlined />} onClick={() => setIsLegendModalVisible(true)}>
            Action Legend
          </Button>
        }
      >
        <TabPane tab="Approved" key="Identities">
          <ObjectList
            title="Approved"
            description="Approved Identities"
            columns={columns}
            actions={actions}
            globalActions={globalActions}
            search={search}
            data={identities}
            pageSize={10}
            onAction={handleAction}
            onGlobalAction={handleAction}
            loading={loading.identities}
            hasMore={pagination.identities.hasMore}
            totalCount={pagination.identities.totalCount}
            useServerPagination={true}
            currentPage={pagination.identities.page}
            onPageChange={handlePageChange}
            searchTerm={searchTerm}
            onSearch={handleSearch}
          />
        </TabPane>
        <TabPane tab="Suspended" key="Suspended">
          <ObjectList
            title="Suspended"
            description="Users under a compliance suspension hold"
            columns={columns}
            actions={suspendedActions}
            search={search}
            data={suspendedIdentities}
            pageSize={10}
            onAction={handleAction}
            loading={loading.suspended}
            hasMore={pagination.suspended.hasMore}
            totalCount={pagination.suspended.totalCount}
            useServerPagination={true}
            currentPage={pagination.suspended.page}
            onPageChange={handlePageChange}
            searchTerm={searchTerm}
            onSearch={handleSearch}
          />
        </TabPane>
        <TabPane tab="Pending" key="Pending">
          <PendingIdentitiesObjectList
            title="Pending"
            description="Identities that have yet to be approved or denied"
            searchTerm={searchTerm}
            columns={pendingColumns}
            actions={pendingActions}
            globalActions={globalActions}
            data={getCurrentData()}
            pageSize={10}
            onAction={handleAction}
            onGlobalAction={handleAction}
            loading={loading.pending}
            hasMore={pagination.pending.hasMore}
            totalCount={pagination.pending.totalCount}
            currentPage={pagination.pending.page}
            onPageChange={handlePageChange}
            onFilterChange={handlePendingFilterChange}
          />
        </TabPane>
        <TabPane tab="Persona Reset" key="Persona Reset">
          <PendingIdentitiesObjectList
            title="Persona Reset"
            description="Users whose Persona verification was reset by an admin — they move back to Pending automatically once they complete re-verification"
            searchTerm={searchTerm}
            columns={pendingColumns}
            actions={personaResetActions}
            globalActions={globalActions}
            data={getCurrentData()}
            pageSize={10}
            onAction={handleAction}
            onGlobalAction={handleAction}
            loading={loading.personaReset}
            hasMore={pagination.personaReset.hasMore}
            totalCount={pagination.personaReset.totalCount}
            currentPage={pagination.personaReset.page}
            onPageChange={handlePageChange}
            showFilterDropdown={false}
          />
        </TabPane>
        <TabPane tab="Under Review" key="Under Review">
          <PendingIdentitiesObjectList
            title="Under Review"
            description="Pending identities currently under review"
            searchTerm={searchTerm}
            columns={pendingColumns}
            actions={underReviewActions}
            globalActions={globalActions}
            data={getCurrentData()}
            pageSize={10}
            onAction={handleAction}
            onGlobalAction={handleAction}
            loading={loading.underReview}
            hasMore={pagination.underReview.hasMore}
            totalCount={pagination.underReview.totalCount}
            currentPage={pagination.underReview.page}
            onPageChange={handlePageChange}
            showFilterDropdown={false}
          />
        </TabPane>
        <TabPane tab="Testers" key="Testers">
          <PendingIdentitiesObjectList
            title="Testers"
            description="Pending identities flagged as testers"
            searchTerm={searchTerm}
            columns={pendingColumns}
            actions={subStateActions}
            globalActions={globalActions}
            data={getCurrentData()}
            pageSize={10}
            onAction={handleAction}
            onGlobalAction={handleAction}
            loading={loading.tester}
            hasMore={pagination.tester.hasMore}
            totalCount={pagination.tester.totalCount}
            currentPage={pagination.tester.page}
            onPageChange={handlePageChange}
            showFilterDropdown={false}
          />
        </TabPane>
        <TabPane tab="Denied" key="Denied">
          <PendingIdentitiesObjectList
            title="Denied"
            description="Identities that have been denied"
            searchTerm={searchTerm}
            columns={pendingColumns}
            actions={deniedActions}
            globalActions={globalActions}
            data={getCurrentData()}
            pageSize={10}
            onAction={handleAction}
            onGlobalAction={handleAction}
            loading={loading.denied}
            hasMore={pagination.denied.hasMore}
            totalCount={pagination.denied.totalCount}
            currentPage={pagination.denied.page}
            onPageChange={handlePageChange}
            showFilterDropdown={false}
          />
        </TabPane>

        <TabPane tab="Removed" key="Removed">
          <PendingIdentitiesObjectList
            title="Removed"
            description="Identities that have been removed"
            searchTerm={searchTerm}
            columns={pendingColumns}
            actions={removedActions}
            globalActions={globalActions}
            data={getCurrentData()}
            pageSize={10}
            onAction={handleAction}
            onGlobalAction={handleAction}
            loading={loading.removed}
            hasMore={pagination.removed.hasMore}
            totalCount={pagination.removed.totalCount}
            currentPage={pagination.removed.page}
            onPageChange={handlePageChange}
            showFilterDropdown={false}
          />
        </TabPane>
        <TabPane tab="Add Rules" key="Claims">
          <ObjectList
            title="Add Rules"
            description="Identities that have yet to be related to Compliance Rules"
            columns={addRulesColumns}
            actions={claimsActions}
            globalActions={globalActions}
            search={search}
            data={getCurrentData()}
            pageSize={10}
            onAction={handleAction}
            onGlobalAction={handleAction}
            loading={loading.claims}
            hasMore={pagination.claims.hasMore}
            totalCount={pagination.claims.totalCount}
            useServerPagination={true}
            currentPage={pagination.claims.page}
            onPageChange={handlePageChange}
            searchTerm={searchTerm}
            onSearch={handleSearch}
          />
        </TabPane>
        <TabPane tab="Reviewers" key="Reviewers">
          <PendingIdentitiesObjectList
            title="Reviewers"
            description="Users with read-only reviewer access"
            searchTerm={searchTerm}
            columns={pendingColumns}
            actions={reviewerActions}
            data={getCurrentData()}
            pageSize={10}
            onAction={handleAction}
            onGlobalAction={handleAction}
            loading={loading.reviewer}
            hasMore={pagination.reviewer.hasMore}
            totalCount={pagination.reviewer.totalCount}
            currentPage={pagination.reviewer.page}
            onPageChange={handlePageChange}
            showFilterDropdown={false}
          />
        </TabPane>
      </Tabs>

      <Modal
        title="Associate Persona Inquiry ID"
        open={isAssociateModalVisible}
        onOk={handleAssociateModalOk}
        onCancel={handleAssociateModalCancel}
        okText="Associate"
        cancelText="Cancel"
      >
        <Form form={associateForm} layout="vertical" name="associateInquiryForm">
          <Form.Item
            name="personaInquiryId"
            label="Persona Inquiry ID"
            rules={[
              {
                required: true,
                message: "Please enter the Persona Inquiry ID",
              },
            ]}
          >
            <Input placeholder="Enter Persona Inquiry ID" />
          </Form.Item>
        </Form>
      </Modal>
      <Modal title="Action Legend" open={isLegendModalVisible} onCancel={() => setIsLegendModalVisible(false)} footer={null}>
        <div className="flex flex-col gap-3 mt-2">
          {visibleLegend.map((item) => (
            <div key={item.label} className="flex items-start gap-3">
              <div className="text-lg flex-shrink-0 mt-1 w-6 text-center">{item.icon}</div>
              <div>
                <div className="font-semibold">{item.label}</div>
                <div className="text-sm text-gray-500">{item.description}</div>
              </div>
            </div>
          ))}
        </div>
      </Modal>

      {/* Investment Request Modal */}
      <InvestmentRequestModal
        visible={investmentModalVisible}
        onClose={() => {
          setInvestmentModalVisible(false);
          setSelectedIdentityForInvestment(null);
        }}
        selectedIdentity={selectedIdentityForInvestment}
        tokenProjects={tokenProjects}
      />
    </>
  );
};

export default IdentitiesPage;
